/** MainCanvas.js
 *
 * Eventually, this might be a React component which manages the main canvas of BMaps.
 * For now, it just manages a reference to the visualizer and contains functions which
 * pass through to the visualizer.
 * This is a first step which allows the previous interface to generally be unchanged.
 *
 * @typedef {import('3dmol-bmaps/src').LabelSpec} LabelSpec
 *
 * @typedef {{x: number, y: number, z: number }} Point
 * @typedef {{
 *     opacity: number?, description: string?, onClick: function?, object: object
 * }} ShapeOptions
 * @typedef CanvasShape
 * @typedef {{
 * text: string,
 * options: LabelSpec,
 * centerAtomGroup: import('./model').AtomGroup | import('./model').Hotspot,
 * }} labelOptions
 * @typedef CanvasLabel
 */
import { VisualizerInterface, NativeCommandStateEvent } from './VisualizerInterface';
import { EventBroker } from './eventbroker';
import { Loader } from './Loader';

let TheVisualizer = new VisualizerInterface();
/**
 * Visualizers constructed on this page, by backendName. Switching back to a backend re-uses its
 * instance (see VisualizerInterface.Deactivate): at most one per backend ever exists.
 */
const visualizerInstances = new Map();
let visualizerOptions = null;
let brokerSubscribed = false;

function makeVisualizer(Backend, viewerOptions) {
    const visualizer = new Backend(viewerOptions);
    visualizer.RegisterMouseHandler(HandleMouseEvent);
    return visualizer;
}

/** Advertise the backend on the wrapper element (e.g. for CSS or tests). */
function advertiseBackend(viewerOptions) {
    if (typeof document !== 'undefined' && viewerOptions?.parentEltId) {
        document.getElementById(viewerOptions.parentEltId)
            ?.setAttribute('visualizer', getVisualizerBackendName());
    }
}

export function viewerMaker(viewerOptions) {
    visualizerOptions = viewerOptions;
    visualizerInstances.clear();
    if (Loader.CanvasVisualizer) {
        TheVisualizer = makeVisualizer(Loader.CanvasVisualizer, viewerOptions);
        visualizerInstances.set(getVisualizerBackendName(), TheVisualizer);
    } else {
        TheVisualizer.RegisterMouseHandler(HandleMouseEvent);
    }
    advertiseBackend(viewerOptions);
    // Once per page: the handlers always talk to the CURRENT visualizer.
    if (!brokerSubscribed) {
        brokerSubscribed = true;
        EventBroker.subscribe('zapAll', () => TheVisualizer.ZapAll());
        EventBroker.subscribe('addAtoms',
            (evtName, {
                atoms, bonds, helices, sheets,
            }) => TheVisualizer.AddAtoms(atoms, bonds, sheets, helices));
        EventBroker.subscribe('removeAtoms',
            (evtName, atoms) => TheVisualizer.RemoveAtoms(atoms));
        EventBroker.subscribe('setAtomSelected',
            (evtName, { atom, selected }) => TheVisualizer.setAtomSelected(atom, selected));
        EventBroker.subscribe('setAtomColor',
            (evtName, { atom, color }) => TheVisualizer.setAtomColor(atom, color));
        EventBroker.subscribe('pauseRedisplay', (evtName, paused) => TheVisualizer.PauseRedisplay(paused));
    }

    return TheVisualizer;
}

/**
 * Test-only: forget every visualizer, so the next viewerMaker() starts from the base class
 * (with no Loader.CanvasVisualizer it keeps the current one, whatever an earlier test installed).
 */
export function resetVisualizerForTests() {
    TheVisualizer = new VisualizerInterface();
    visualizerInstances.clear();
    visualizerOptions = null;
}

/** @returns {string[]} backendNames this build offers (Loader.CanvasVisualizers) */
export function availableVisualizerBackends() {
    return Object.keys(Loader.CanvasVisualizers || {});
}

/**
 * Make another backend the active visualizer (Preferences -> 3D viewer). This is the low-level
 * swap: the current visualizer is emptied and hidden (Deactivate), the other one - constructed
 * on first use, re-used afterwards - is shown (Activate) and becomes the target of every
 * MainCanvas call and broker event. The scene is NOT rebuilt here: display_mgr does that from
 * the model afterwards (switchVisualizerBackend in display_mgr.js).
 * @param {string} name a backendName from availableVisualizerBackends()
 * @returns {boolean} true if the active visualizer changed
 */
export function switchVisualizer(name) {
    const Backend = (Loader.CanvasVisualizers || {})[name];
    if (!Backend) {
        console.warn(`switchVisualizer: no 3D visualizer "${name}" in this build (${availableVisualizerBackends().join(', ')})`);
        return false;
    }
    if (name === getVisualizerBackendName()) return false;
    if (!visualizerSupported(name)) {
        console.warn(`switchVisualizer: "${name}" cannot run in this browser (needs ${Backend.requirement})`);
        return false;
    }
    const previous = TheVisualizer;
    // Construct the other backend BEFORE emptying this one: display_mgr has already forgotten
    // every visualization by now and redraws into whatever is active afterwards, so a failing
    // constructor (no WebGL context, a canvas that cannot be made) must leave a visualizer that
    // still draws, not a deactivated one.
    let next = visualizerInstances.get(name);
    if (!next) {
        try {
            next = makeVisualizer(Backend, visualizerOptions || {});
        } catch (ex) {
            console.error(`switchVisualizer: could not construct "${name}"; staying on ${getVisualizerBackendName()}`, ex);
            return false;
        }
        visualizerInstances.set(name, next);
    }
    try {
        previous.Deactivate();
    } catch (ex) {
        console.error(`switchVisualizer: deactivating ${getVisualizerBackendName()} failed`, ex);
    }
    // A switch inside withPausedRedisplay(): the matching unpause must reach the new visualizer.
    next.redisplayPauseLevel = previous.redisplayPauseLevel || 0;
    previous.redisplayPauseLevel = 0;
    TheVisualizer = next;
    next.Activate();
    advertiseBackend(visualizerOptions);
    console.log(`3D visualizer switched to ${name}`);
    // The Commands panel shows or hides its viewer tab from this.
    EventBroker.publish(NativeCommandStateEvent, next.NativeCommandState());
    return true;
}

/** @returns {boolean} whether the named backend can run in this browser (isSupported()) */
export function visualizerSupported(name) {
    const Backend = (Loader.CanvasVisualizers || {})[name];
    return !!Backend && (typeof Backend.isSupported !== 'function' || Backend.isSupported());
}

/**
 * Drop an INACTIVE visualizer for good (Dispose) so that choosing its backend again constructs
 * a fresh one - e.g. after its payload failed to load. The active visualizer is never dropped.
 * @returns {boolean} whether an instance was dropped
 */
export function forgetVisualizer(name) {
    const instance = visualizerInstances.get(name);
    if (!instance || instance === TheVisualizer) return false;
    visualizerInstances.delete(name);
    try {
        instance.Dispose();
    } catch (ex) {
        console.warn(`forgetVisualizer: disposing ${name} failed`, ex);
    }
    return true;
}

// Helpers
/* HandleMouseEvent(): Mouse handler registered to the visualizer.
 * Signature:
 *   - targetObject - { background: true } | { atom: <targetAtom> } | { shape: <targetShape> }
 *   - mouseEvent   - Javascript mouse event
 */
function HandleMouseEvent(targetObject, mouseEvent) {
    if (targetObject.background) {
        EventBroker.publish('backgroundMouse', mouseEvent);
    } else if (targetObject.atom) {
        EventBroker.publish('atomMouse', { atom: getBMapsAtom(targetObject.atom), mouseEvent });
    } else if (targetObject.shape) {
        EventBroker.publish('shapeMouse', { selected: targetObject.shape, mouseEvent });
    } else {
        console.warn(`Unknown mouse target for ${mouseEvent.type}: ${JSON.stringify(targetObject)}`);
    }
}

// Pass-throughs which preserve the current interface

/** @returns {string} the active backend's identifier, e.g. '3dmol' */
export function getVisualizerBackendName() {
    return TheVisualizer.constructor.backendName;
}

export function resizeCanvas(width, height) {
    TheVisualizer.ResizeViewer(width, height);
}

export function getBMapsAtom(atom) {
    return TheVisualizer.AppAtom(atom);
}

export function getCanvasAtom(atom) {
    return TheVisualizer.VisAtom(atom);
}

export function getCameraPosition() {
    return TheVisualizer.GetCameraPosition();
}

export function setCameraPosition(cameraPosition) {
    TheVisualizer.SetCameraPosition(cameraPosition);
}

export function rotateView(degrees, axis, pivot) {
    TheVisualizer.RotateView(degrees, axis, pivot);
}

export function zoomView(factor) {
    TheVisualizer.ZoomView(factor);
}

export function panView(delta) {
    TheVisualizer.PanView(delta);
}

export function resetCameraOrientation() {
    TheVisualizer.ResetCameraOrientation();
}

export function addAtoms(atoms, bonds, helices, sheets) {
    TheVisualizer.AddAtoms(atoms, bonds, helices, sheets);
}

export function setAtomStyle(atom, style) {
    TheVisualizer.StyleAtom(atom, style);
}

export function setMoleculeDisplayStyle(style, selectorFn) {
    TheVisualizer.StyleAtoms(style, selectorFn);
}

export function setBackgroundColor(colorInfo) {
    TheVisualizer.SetBackgroundColor(colorInfo);
}

/**
 * Refresh the display of existing items in the canvas, including atom colors and bonds.
 * This does not add or remove atoms.
 */
export function redisplay() {
    TheVisualizer.Redisplay();
}

export function zoomToAtoms(selectorFn) {
    TheVisualizer.ZoomToAtoms(selectorFn);
}

export function drawSurface(surfaceArgs) {
    return TheVisualizer.DrawSurface(surfaceArgs);
}

export function removeSurface(surface) {
    TheVisualizer.RemoveSurface(surface);
}

/**
 * Draw a sphere on the canvas
 * @param {Point} center
 * @param {number} radius
 * @param {string|number} color
 * @param {ShapeOptions} shapeOptions
 * @returns {CanvasShape}
 */
export function drawSphere(center, radius, color, shapeOptions) {
    return TheVisualizer.drawSphere(center, radius, color, shapeOptions);
}

export function removeSphere(sphere) {
    TheVisualizer.removeSphere(sphere);
}

/**
 * Draw a cylinder on the canvas
 * @param {Point} start
 * @param {Point} end
 * @param {number} radius
 * @param {string|number} color
 * @param {boolean} dashed
 * @param {ShapeOptions} shapeOptions
 * @returns {CanvasShape}
 */
export function drawCylinder(start, end, radius, color, dashed, shapeOptions) {
    return TheVisualizer.drawCylinder(start, end, radius, color, dashed, shapeOptions);
}

export function removeCylinder(cylinder) {
    TheVisualizer.removeCylinder(cylinder);
}

/**
 * Draw an arrow on the canvas
 * @param {Point} start
 * @param {Point} end
 * @param {number} radius
 * @param {string|number} color
 * @param {number?} radiusRatio
 * @param {ShapeOptions} shapeOptions
 * @returns {CanvasShape}
 */
export function drawArrow(start, end, radius, color, radiusRatio, shapeOptions) {
    return TheVisualizer.drawArrow(start, end, radius, color, radiusRatio, shapeOptions);
}

export function removeArrow(arrow) {
    TheVisualizer.removeArrow(arrow);
}

export function removeShape(shape) {
    TheVisualizer.RemoveShape(shape);
}

export function setAtomSelected(atom, selected) {
    TheVisualizer.setAtomSelected(atom, selected);
}

export function setAtomColor(atom, color) {
    TheVisualizer.setAtomColor(atom, color);
}

export function atomIsVisible(atom) {
    return TheVisualizer.atomIsVisible(atom);
}

/** Let backends with per-residue cartoon coloring resolve colors after a full display pass. */
export function refreshCartoonColors() {
    TheVisualizer.refreshCartoonColors();
}

/**
 * Capture the 3D canvas as an image.
 * @param {{type?: string, quality?: number}} [options]
 * @returns {Promise<Blob|null>}
 */
export function getImageBlob(options) {
    return TheVisualizer.GetImageBlob(options);
}

/** @returns {string[]} the rep-set representations the current viewer draws */
export function supportedRepSetReps() {
    return TheVisualizer.SupportedRepSetReps();
}

/**
 * @returns {string[]} the rep-set representations the current viewer makes transparent per
 *     group
 */
export function perGroupTransparentReps() {
    return TheVisualizer.PerGroupTransparentReps();
}

/** Whether getImageBlob({ray: true, width, dpi, outline, transparentBackground}) is available. */
export function supportsRayTracedImage() {
    return TheVisualizer.SupportsRayTracedImage();
}

/**
 * The pixel size a ray-traced export would have, for `{width}` pixels across or `{scale}` x the
 * canvas - the backend caps it - or null when there is nothing to render.
 */
export function rayTracedImageSize(request) {
    return TheVisualizer.RayTracedImageSize(request);
}

/** Whether the visualizer can write its own session file (PyMOL: a `.pse`). */
export function supportsNativeSessionExport() {
    return TheVisualizer.SupportsNativeSessionExport();
}

/** @returns {Promise<{blob: Blob, extension: string, format: string}|null>} */
export function saveNativeSession() {
    return TheVisualizer.SaveNativeSession();
}

/** The `CartoonStyle` preference ('pymol' | 'maestro'); the caller redraws afterwards. */
export function setCartoonStyle(name) {
    TheVisualizer.SetCartoonStyle(name);
}

/** The `ColorStyle` preference ('bmaps' | 'maestro'); the caller redraws afterwards. */
export function setColorStyle(name) {
    TheVisualizer.SetColorStyle(name);
}

// Native command line of the backend (see VisualizerInterface.RunNativeCommand)

export function supportsNativeCommands() {
    return TheVisualizer.SupportsNativeCommands();
}

export function runNativeCommand(text) {
    return TheVisualizer.RunNativeCommand(text);
}

export function completeNativeCommand(text) {
    return TheVisualizer.CompleteNativeCommand(text);
}

export function nativeCommandState() {
    return TheVisualizer.NativeCommandState();
}

export function makeTempMolecule(atoms, style, colorMap, opacity, atomPair) {
    return TheVisualizer.MakeTempMolecule(atoms, style, colorMap, opacity, atomPair);
}

export function removeTempMolecule(model) {
    return TheVisualizer.RemoveTempMolecule(model);
}

export function updateTempMolecule(model, arg) {
    return TheVisualizer.UpdateTempMolecule(model, arg);
}

export function removeAtoms(atoms) {
    return TheVisualizer.RemoveAtoms(atoms);
}

/**
 * @param {labelOptions} labelOptions
 */
export function drawLabel(labelOptions) {
    return TheVisualizer.drawLabel(labelOptions);
}

export function removeLabel(label) {
    return TheVisualizer.removeLabel(label);
}

export function resetMainCanvas() {
    return TheVisualizer.Reset();
}
